import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { cameraEasing, getDensitySnapshot, getFloorLayerOptions, getSpaceDensityLayerOptions, simpleEaseOutQuad } from './utils';

import { DensityMapContainer } from './container';
import { Query, ResultSet, TimeDimension } from '@cubejs-client/core';
import { SiteFeatures } from '../map-data-utils';
import { DensityMapConfigurator } from './';
import { useDensityMapControls } from '../use-density-map-controls';
import { ClockGranularity } from '../clock';

type DensitySnapshot = {
  features: { [spaceRef: string]: number};
  range: {
    min: number;
    max: number;
  };
}

export type DensityMapProps = {
  level?: number;
  densityData: ResultSet<any>;
  site: SiteFeatures;
  config: DensityMapConfigurator;
  playbackControl?: boolean;
  fullscreenControl?: boolean;
};

const pivotConfig = {
  x: ['ViewDensity.hour', 'ViewDensity.space_ref'],
  y: ['measures'],
  fillMissingDates: true
}

const getMapOptions = (config: DensityMapConfigurator) => ({
  style: config.mapStyle || fallbackMapStyle,
  attributionControl: false,
  pitch: config.camera.pitch,
})

const extractTimeDimension = (query: Query) => {
  const timeDimensions = query.timeDimensions && query.timeDimensions[0];
  if (!timeDimensions) throw new Error("Could not extract time dimensions from query");
  return timeDimensions;
}

const extractDateRange = (timeDimensions: TimeDimension) => {
  const dateRange = timeDimensions && timeDimensions.dateRange;
  if (!dateRange) throw new Error("Could not extract date range from time query dimensions");
  const fromToArray = Array.isArray(dateRange) ? dateRange : [dateRange, dateRange];
  return { from: fromToArray[0], to: fromToArray[1]}
}

const extractGranularity = (timeDimensions: TimeDimension): ClockGranularity => {
  const granularity = timeDimensions.granularity;
  const defaultGranularity = 'hour';

  switch (granularity) {
    case 'hour':
    case 'day':
    case 'week':
      return granularity;
    default:
      console.log(`Setting query granularity '${granularity}' to clock granularity '${defaultGranularity}'`)
      return defaultGranularity
  }
}
/**
 * MapStyle
 * Prefer value from Config file if available.
 * Fallback to environment vars or default mapbox style.
 */
const fallbackMapStyle = 'mapbox://styles/mapbox/streets-v11';

const DensityMap: React.FC<DensityMapProps> = ({level, densityData, site, playbackControl, fullscreenControl, config}) => {
  
  const mapRef = useRef(null);
  const [map, setMap] = useState<mapboxgl.Map>();
  const [visitorDensity] = useState(densityData.chartPivot(pivotConfig));
  
  const [featureCollection, setFeatureCollection] = useState(site.featureCollection);
  const levels = level !== undefined ? [level] : site.levelNumbers;

  const query = densityData.query();
  const timeDimension = extractTimeDimension(query)
  const dateRange = extractDateRange(timeDimension);
  const granularity = extractGranularity(timeDimension);

  /**
   * 
   * @param msUntilNextUpdate {number} used to determine duration before next update event can be expected. when this is zero, it means the autopilot has been turned off.
   * @returns null
   */
  const onAutopilotUpdate = (msUntilNextUpdate: number) => {
    if (!map) return;
    const bearing = msUntilNextUpdate > 0 ? map.getBearing() + 2 : map.getBearing()
    map.easeTo({
      bearing,
      duration: msUntilNextUpdate,
      easing: cameraEasing
    });
  }

  const onTimelineUpdate = (timestamp?: string) => {
    const timeframeSnapshot = getDensitySnapshot({
      visitorDensity,
      granularity,
      timestamp
    })
    renderDensityData(timeframeSnapshot)
  }

  const { densityMapControls } = useDensityMapControls({
    dateRange,
    onAutopilotUpdate,
    onTimelineUpdate
  })
  
  useEffect(() => {
    const container = mapRef && mapRef.current;
    if (!container) return;
    
    var map = new mapboxgl.Map({container, ...getMapOptions(config)});
    map.on('load', () => {
      initialiseMap(map) 
    });
    
    // add zoom and rotate controls to the map
    map.addControl(new mapboxgl.NavigationControl(), 'top-left');

    // if we have a stacked levels view, enable custom controls and fullscreen button
    playbackControl && map.addControl(densityMapControls, "bottom-left");
    fullscreenControl && map.addControl(new mapboxgl.FullscreenControl());
    
    return  () => {
      map.remove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef]);
  

  const initialiseMap = (map: mapboxgl.Map) => {

    // connect the site's geojson information to the map's data source
    map.addSource('spaces', {
      type: 'geojson',
      data: featureCollection
    });

    // add spaces and floor layers for each level in the map
    levels.forEach(level => {
      const spaceDensityLayerOptions = getSpaceDensityLayerOptions('spaces', level);
      const floorLayerOptions = getFloorLayerOptions('spaces', config, level);
      map.addLayer(spaceDensityLayerOptions);
      map.addLayer(floorLayerOptions);
    })

    // set the camera position and zoom level to best fit the
    // features rendered onto the map's layers
    map.fitBounds(getMapBoundary(), {padding: 25, duration: 0 });
    
    setMap(map);
  }
  
  useEffect(() => {
    renderDensityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[map])

  /**
   * If we are only displaying a single level, use the boundaries of spaces on that level only
   * to set the map's zoom and camera centre. If there is more then one level being displayed, use the site's
   * full boundary values.
   * @returns {mapboxgl.LngLatBounds}
   */
  const getMapBoundary = () => {
    if (levels.length > 1) return site.bounds;
    
    const boundaryCoords = featureCollection.features.reduce((acc: any, feature) => levels.includes(feature.properties.level) ? [...acc, ...feature.geometry.coordinates[0]] : acc,[]);
    
    
    const boundary = boundaryCoords.reduce(function(bounds: any, coord: any) {
      return bounds.extend(coord);
    }, new mapboxgl.LngLatBounds(boundaryCoords[0], boundaryCoords[0])); 
    
    return boundaryCoords.length > 0 ?  boundary : site.bounds;
  }
  
  /**
   * 
   * @param snapshot {DensitySnapshot} Optional snapshot of density data at a specific moment in time. If not supplied, the renderDensityData method with display total density data for the entire query period.
   */
  const renderDensityData = (densitySnapshot?: DensitySnapshot) => {
    
    // if no snapshot supplied as argument, get a snapshot for the entire query timeframe
    const snapshot = densitySnapshot || getDensitySnapshot({visitorDensity, granularity});

    const getProportionateDensity = (actual: number) => {
      const { min, max } = snapshot.range;
      return Math.round((actual - min) / (max - min) * 100) / 100
    }

    const hueVariance = 50;

    const updatedFeatures = featureCollection.features.map(space => {  
      const visitorCount = snapshot.features[space.id] || 0;
      const { baseHeight, maxHeight } = space.properties;
      
      const density = visitorCount && getProportionateDensity(visitorCount) || 0;
      const calculatedHeight = baseHeight + maxHeight * density;
      const [h, s, l] = space.properties.fillHSL;
      
      
      const lVAmount = (l/100) * 50;

      const newS = simpleEaseOutQuad(s/100 * density) * 100;
      const newH = (h - hueVariance/2)  +  (density * hueVariance);
      const newL = l + (lVAmount/2) - (density * lVAmount);
      const hsl = `hsl(${newH},${newS}%,${newL}%)`;

      
      space.properties.height = calculatedHeight;
      space.properties.color = hsl;

      return space;
    });

    const newData = { ...featureCollection, features: updatedFeatures };

    // @ts-ignore
    map && map.getSource('spaces').setData(newData);
    setFeatureCollection(newData);
  }

  return <DensityMapContainer ref={mapRef} />;
};

export { DensityMap };